import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Font options for email
export const fontOptions = [
  { name: 'Helvetica', value: 'helvetica, "helvetica neue", arial, verdana, sans-serif' },
  { name: 'Arial', value: 'arial, "helvetica neue", helvetica, sans-serif' },
  { name: 'Georgia', value: 'georgia, times, "times new roman", serif' },
  { name: 'Tahoma', value: 'tahoma, verdana, segoe, sans-serif' },
  { name: 'Verdana', value: 'verdana, geneva, sans-serif' },
  { name: 'Trebuchet MS', value: '"trebuchet ms", "lucida grande", "lucida sans unicode", "lucida sans", sans-serif' }
];

// Default style settings
const defaultStyles = {
  primaryColor: '#333333',
  secondaryColor: '#a1894c',
  backgroundColor: '#ffffff',
  fontFamily: 'helvetica, "helvetica neue", arial, verdana, sans-serif',
  buttonTextColor: '#ffffff',
};

// Create context with initial value to prevent undefined errors
const EmailStyleContext = createContext({
  styles: defaultStyles,
  updateStyles: () => {},
  fontOptions: fontOptions
});

export const EmailStyleProvider = ({ children, initialStyles = {} }) => {
  const [styles, setStyles] = useState({
    ...defaultStyles,
    ...initialStyles
  });

  // Force update when initialStyles change
  useEffect(() => {
    setStyles({
      ...defaultStyles,
      ...initialStyles
    });
  }, [initialStyles.primaryColor, initialStyles.secondaryColor, initialStyles.backgroundColor, initialStyles.fontFamily]);

  // Apply global email styles
  useEffect(() => {
    const styleId = "email-global-styles";
    let styleEl = document.getElementById(styleId);
    
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }
    
    // Log current styles for debugging
    
    // Define global styles based on the current theme
    styleEl.innerHTML = `
      :root {
        --email-primary-color: ${styles.primaryColor};
        --email-secondary-color: ${styles.secondaryColor};
        --email-background-color: ${styles.backgroundColor || '#ffffff'};
        --email-font-family: ${styles.fontFamily};
        --email-button-text-color: ${styles.buttonTextColor || '#ffffff'};
      }
    
      .product-section, .email-section {
        font-family: ${styles.fontFamily} !important;
      }
      .email-content-container {
        font-family: ${styles.fontFamily} !important;
        // background-color: ${styles.backgroundColor || '#ffffff'};
      }
      /* Apply background color to main section tables */
      .draggable-section > table,
      .draggable-section > *:not(.component-controls) > table:first-of-type,
      .product-section > table,
      .email-section > table
       {
        background-color: ${styles.backgroundColor || '#ffffff'} !important;
      }
      .product-section h1, .product-section h2, .product-section h3, 
      .product-section h4, .product-section h5, .product-section h6,
      .email-section h1, .email-section h2, .email-section h3, 
      .email-section h4, .email-section h5, .email-section h6 {
        color: ${styles.secondaryColor} !important;
      }
      .product-section p, .product-section div:not(.section-header),
      .email-section p, .email-section div:not(.section-header) {
        color: ${styles.primaryColor} !important;
      }
      .email-header p {
        color: ${styles.primaryColor} !important;
      }
      .email-header p strong {
        color: ${styles.secondaryColor} !important;
      }
      .description-text, .productDescription {
        color: ${styles.primaryColor} !important;
      }
      
      /* Add button styles */
      .email-button, .email-cta {
        background-color: ${styles.secondaryColor} !important;
        color: ${styles.buttonTextColor || '#ffffff'} !important;
      }
    `;
    
    return () => {
      // Safely handle cleanup to prevent "removeChild" errors
      try {
        if (styleEl && document.getElementById(styleId)) {
          // Check if element is actually a child of document.head before removing
          if (styleEl.parentNode === document.head) {
            document.head.removeChild(styleEl);
          }
        }
      } catch (error) {
      }
    };
  }, [styles]);

  const updateStyles = useCallback((newStyles) => {
    setStyles(prev => ({ ...prev, ...newStyles }));
  }, []);

  const value = {
    styles,
    updateStyles,
    fontOptions
  };

  return (
    <EmailStyleContext.Provider value={value}>
      {children}
    </EmailStyleContext.Provider>
  );
};

// Export hook with a fallback to prevent undefined errors
export const useEmailStyles = () => {
  const context = useContext(EmailStyleContext);
  if (!context) {
    console.warn('useEmailStyles must be used within an EmailStyleProvider');
    // Return default values to prevent destructuring errors
    return {
      styles: defaultStyles,
      updateStyles: () => {},
      fontOptions: fontOptions
    };
  }
  return context;
}; 